// Date-only helpers. Date-only values are 'YYYY-MM-DD' strings in the viewer's local calendar.
// Arithmetic on them is done on UTC dates internally, so it is immune to DST and timezone shifts.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_MS = 86400000;

const pad = (n, w = 2) => String(n).padStart(w, '0');

function parts(s) {
  const [y, m, d] = s.split('-').map(Number);
  return { y, m, d };
}

// Date.UTC maps years 0–99 to 1900–1999; setUTCFullYear does not.
function utcDate(y, m0, d) {
  const dt = new Date(0);
  dt.setUTCFullYear(y, m0, d);
  return dt.getTime();
}

function daysInMonth(y, m) {
  return new Date(utcDate(y, m, 0)).getUTCDate();
}

// 'YYYY-MM-DD' → ms since epoch of that date at UTC midnight (a pure day counter).
function utcMs(s) {
  const { y, m, d } = parts(s);
  return utcDate(y, m - 1, d);
}

function fromUtcMs(ms) {
  const dt = new Date(ms);
  return `${pad(dt.getUTCFullYear(), 4)}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

export function isISODate(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const { y, m, d } = parts(s);
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);
}

// Dates the app accepts from people: years 2000–2100 (a typo such as 0026 or 9999 is refused).
export const MIN_YEAR = 2000;
export const MAX_YEAR = 2100;
export function isSupportedYear(s) {
  const y = Number(String(s).slice(0, 4));
  return y >= MIN_YEAR && y <= MAX_YEAR;
}

export function toISODate(date) {
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseISODate(s) {
  const { y, m, d } = parts(s);
  return new Date(y, m - 1, d);
}

export function todayISO() {
  return toISODate(new Date());
}

export function addDays(s, n) {
  const { y, m, d } = parts(s);
  return fromUtcMs(utcDate(y, m - 1, d + n));
}

export function addMonths(s, n) {
  const { y, m, d } = parts(s);
  const total = y * 12 + (m - 1) + n;
  const ty = Math.floor(total / 12);
  const tm = total - ty * 12 + 1;
  return `${pad(ty, 4)}-${pad(tm)}-${pad(Math.min(d, daysInMonth(ty, tm)))}`;
}

export function diffDays(a, b) {
  return Math.round((utcMs(b) - utcMs(a)) / DAY_MS);
}

function weekday(s) {
  return new Date(utcMs(s)).getUTCDay(); // 0 = Sunday
}

// 'mon' … 'sun' for a 'YYYY-MM-DD' date (timezone-safe; matches constants WEEKDAY_KEYS).
const WEEKDAY_KEYS_BY_DAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export function weekdayKey(s) {
  return WEEKDAY_KEYS_BY_DAY[weekday(s)];
}

export function startOfWeek(s) {
  return addDays(s, -((weekday(s) + 6) % 7));
}

export function endOfWeek(s) {
  return addDays(startOfWeek(s), 6);
}

export function startOfMonth(s) {
  return `${s.slice(0, 7)}-01`;
}

export function endOfMonth(s) {
  const { y, m } = parts(s);
  return `${s.slice(0, 7)}-${pad(daysInMonth(y, m))}`;
}

export function isoDateOfTimestamp(iso) {
  if (!iso) return '';
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? '' : toISODate(dt);
}

export function datePart(v) {
  return typeof v === 'string' ? v.slice(0, 10) : '';
}

export function timePart(v) {
  const m = typeof v === 'string' ? /T(\d{2}:\d{2})/.exec(v) : null;
  return m ? m[1] : '';
}

export function formatDate(s, style = 'short') {
  const date = datePart(s);
  if (!isISODate(date)) return '';
  const { y, m, d } = parts(date);
  const mon = MONTHS[m - 1];
  const wd = WEEKDAYS[weekday(date)];
  switch (style) {
    case 'long': return `${mon.slice(0, 3)} ${d}, ${y}`;
    case 'weekday': return `${wd.slice(0, 3)}, ${mon.slice(0, 3)} ${d}`;
    case 'full': return `${wd}, ${mon} ${d}, ${y}`;
    default: return `${mon.slice(0, 3)} ${d}`;
  }
}

// 'Sep 21 – Oct 20, 2026' (the first year is dropped when both dates share it); one date → 'Sep 21, 2026'.
export function formatDateRange(start, end) {
  if (!isISODate(datePart(start)) || !isISODate(datePart(end))) return '';
  if (datePart(start) === datePart(end)) return formatDate(end, 'long');
  const sameYear = datePart(start).slice(0, 4) === datePart(end).slice(0, 4);
  return `${formatDate(start, sameYear ? 'short' : 'long')} – ${formatDate(end, 'long')}`;
}

export function formatMonthYear(s) {
  const date = datePart(s);
  if (!isISODate(date)) return '';
  const { y, m } = parts(date);
  return `${MONTHS[m - 1]} ${y}`;
}

export function formatTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

export function relativeDay(s, today) {
  const n = diffDays(today, datePart(s));
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n === -1) return 'Yesterday';
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

export function monthRange(clientStartDate, monthNumber) {
  return {
    startDate: addMonths(clientStartDate, monthNumber - 1),
    endDate: addDays(addMonths(clientStartDate, monthNumber), -1),
  };
}

// The client month (1–count) whose range contains `date`, clamped: before month 1 → 1, after the last → count
// (default 99 = MAX_MONTH: a project can run past its contract).
export function monthNumberForDate(clientStartDate, date, count = 99) {
  if (!clientStartDate || !date || date < clientStartDate) return 1;
  // Jump close to the answer first (a month is 28–31 days), then walk: cheap even for month 99.
  const guess = Math.max(1, Math.min(count, Math.floor(diffDays(clientStartDate, date) / 31)));
  for (let n = guess; n <= count; n++) {
    if (date <= monthRange(clientStartDate, n).endDate) return n;
  }
  return count;
}
